/**
 * EshopScannerPage.jsx
 * ------------------------------------------------------------------
 * Standalone landing for the E-shop GEO/SEO Specialist bulk scanner.
 * Renders at `/eshop-scanner` (and is also embedded as a tab inside
 * the user Dashboard at `?tab=eshop-scanner`).
 *
 * Two main states:
 *   1. No active report → input form (domain + max_urls + scan CTA)
 *      with the user's E-shop quota widget right next to it.
 *   2. Report ready → renders `<EshopScanReport />` with the full
 *      breakdown + per-row AI Fix buttons.
 *
 * Scan is synchronous (30–60s wait). We display a step-based loading
 * screen so the wait feels intentional rather than dead air. If we ever
 * move to async + polling, this is the only place that needs swapping.
 *
 * Auth model: re-uses the rest of the app's pattern — `useAuth()` gives
 * us `user.email` for the `X-User-Email` header. Anonymous visitors get
 * a sign-in CTA — scans burn quota so we don't run them anonymously.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import {
  Search, Loader2, ShoppingCart, ArrowRight, AlertCircle,
  Sparkles, Globe, TrendingUp, History,
} from "lucide-react";
import EshopUsageWidget from "../components/EshopUsageWidget";
import EshopScanReport from "../components/eshop/EshopScanReport";
import EshopGeoGenerator from "../components/eshop/EshopGeoGenerator";

const LOADING_STEPS = [
  "Hľadám sitemap.xml + robots.txt…",
  "Klasifikujem URL na produkty / kategórie / blog…",
  "Sťahujem stránky a analyzujem schema/meta…",
  "Skladám hodnotenie + top issues report…",
];

export default function EshopScannerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const initialReportId = search.get("report") || "";

  const [domain, setDomain] = useState("");
  const [maxUrls, setMaxUrls] = useState(100);
  const [scanning, setScanning] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);

  const userEmail = user?.email || "";

  // Walk through the loading steps every ~4s so the user sees progress.
  // Caps at the last step so it doesn't loop confusingly.
  useEffect(() => {
    if (!scanning) { setLoadingStep(0); return; }
    const id = setInterval(() => {
      setLoadingStep(s => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 4500);
    return () => clearInterval(id);
  }, [scanning]);

  // Load prior reports for the sidebar history (last 10). On first
  // mount with no `?report=` param, also auto-load the most recent
  // report so the Mass Page Generator + report card are visible
  // immediately rather than hidden behind a "click Otvoriť" gate.
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    axios.get(`${API}/eshop-seo/reports`, {
      headers: { "X-User-Email": userEmail },
      params: { limit: 10 },
    }).then(r => {
      if (cancelled) return;
      const list = r.data?.reports || [];
      setHistory(list);
      if (!initialReportId && !report && list.length > 0) {
        // Auto-hydrate with the latest scan so the generator surfaces.
        axios.get(`${API}/eshop-seo/reports/${list[0].id}`, {
          headers: { "X-User-Email": userEmail },
        }).then(rr => { if (!cancelled) setReport(rr.data); })
          .catch(() => {});
      }
    }).catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [userEmail, initialReportId, report?.id]);

  // If the URL has `?report=<id>` on first load, fetch that report.
  useEffect(() => {
    if (!initialReportId || !userEmail || report?.id === initialReportId) return;
    axios.get(`${API}/eshop-seo/reports/${initialReportId}`, {
      headers: { "X-User-Email": userEmail },
    }).then(r => setReport(r.data)).catch(() => setReport(null));
  }, [initialReportId, userEmail, report?.id]);

  const handleScan = async () => {
    if (!domain.trim()) return;
    if (!userEmail) { navigate("/login?redirect=/eshop-scanner"); return; }
    setScanning(true); setError(null); setReport(null);
    try {
      const r = await axios.post(`${API}/eshop-seo/scan`,
        { domain: domain.trim(), max_urls: maxUrls },
        {
          headers: { "X-User-Email": userEmail },
          timeout: 120000,  // big shops can take a minute even with concurrency cap
        },
      );
      setReport(r.data);
      // Bake the report id into the URL so refresh-resistance + shareable.
      navigate(`/eshop-scanner?report=${r.data.id}`, { replace: true });
    } catch (e) {
      // Quota-exceeded comes back as structured 402 JSON. Hand it to the
      // dedicated banner so the user sees a clean upgrade CTA rather
      // than raw HTTP detail.
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && typeof d === "object") {
        setError({ type: "quota", payload: d });
      } else if (e?.response?.status === 400 && typeof d === "object") {
        setError({ type: "no_sitemap", payload: d });
      } else if (e?.response?.status === 401) {
        navigate("/login?redirect=/eshop-scanner");
      } else {
        setError({ type: "generic", message: typeof d === "string" ? d : "Scan zlyhal. Skús to znova." });
      }
    } finally {
      setScanning(false);
    }
  };

  const handleLoadHistory = async (id) => {
    if (!userEmail) return;
    setReport(null); setError(null);
    try {
      const r = await axios.get(`${API}/eshop-seo/reports/${id}`, {
        headers: { "X-User-Email": userEmail },
      });
      setReport(r.data);
      navigate(`/eshop-scanner?report=${id}`, { replace: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {}
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="eshop-scanner-page">
      <header className="bg-black/80 backdrop-blur-xl border-b border-gray-800 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-black tracking-tight">TopRankPulse</span>
          </a>
          <nav className="flex items-center gap-4 text-xs uppercase tracking-widest text-gray-400">
            <a href="/dashboard" className="hover:text-white">Dashboard</a>
            <a href="/pricing#eshop" className="hover:text-white">E-shop balíky</a>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Hero */}
        <section className="mb-8" data-testid="eshop-scanner-hero">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingCart className="w-4 h-4 text-purple-400" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-purple-400">
              E-shop GEO/SEO Specialist
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[0.95] mb-3 break-words" style={{ textWrap: "balance" }}>
            Naskenuj celý e-shop. Zisti <span className="text-purple-400">každý chýbajúci schema</span> za 60 sekúnd.
          </h1>
          <p className="text-gray-400 max-w-2xl">
            Sitemap-driven bulk scan: produkty, kategórie, blog. Per-URL známka A–F,
            top issues a AI-generated copy-paste fix pre každú jednu chybu.
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Scan form */}
          <div className="lg:col-span-2 p-6 bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-sm">
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Doména e-shopu
                </label>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500 shrink-0" />
                  <input
                    data-testid="eshop-scan-domain-input"
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleScan(); }}
                    placeholder="https://mojeshop.sk"
                    className="flex-1 px-3 py-2 bg-black/50 border border-gray-800 focus:border-purple-500 rounded-sm text-white placeholder-gray-600 font-mono text-sm focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">
                  Max URLs na scan: <span className="text-purple-400 font-bold tabular-nums">{maxUrls}</span>
                </label>
                <input
                  data-testid="eshop-scan-maxurls-slider"
                  type="range"
                  min={10} max={500} step={10}
                  value={maxUrls}
                  onChange={(e) => setMaxUrls(Number(e.target.value))}
                  className="w-full accent-purple-500"
                />
                <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                  <span>10</span><span>100</span><span>250</span><span>500</span>
                </div>
              </div>

              <button
                data-testid="eshop-scan-submit-btn"
                onClick={handleScan}
                disabled={scanning || !domain.trim()}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 transition-all"
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Scanujem…</>
                ) : (
                  <><Search className="w-4 h-4" /> Spustiť bulk scan <ArrowRight className="w-4 h-4" /></>
                )}
              </button>

              <p className="text-[10px] text-gray-500">
                Každá URL = 1 quota credit z balíka „Produktov scanovaných / mes“.
                Bez plánu? <a href="/pricing#eshop" className="text-purple-400 hover:underline">Pozri E-shop balíky →</a>
              </p>
            </div>
          </div>

          {/* Quota widget */}
          <div className="lg:col-span-1">
            <EshopUsageWidget userEmail={userEmail} />
          </div>
        </div>

        {/* Loading screen */}
        {scanning && (
          <div className="p-8 bg-gradient-to-br from-purple-900/20 to-black border border-purple-700/40 rounded-sm mb-8" data-testid="eshop-scan-loading">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
              <div className="text-sm font-bold">{LOADING_STEPS[loadingStep]}</div>
            </div>
            <div className="space-y-1">
              {LOADING_STEPS.map((step, i) => (
                <div key={i} className={`text-xs ${i <= loadingStep ? "text-purple-300" : "text-gray-600"}`}>
                  {i < loadingStep ? "✓" : i === loadingStep ? "→" : "·"} {step}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error states */}
        {error?.type === "quota" && (
          <div className="p-6 bg-rose-900/20 border border-rose-700/40 rounded-sm mb-8" data-testid="eshop-scan-quota-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-400 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-rose-200 mb-1">Mesačná kvóta vyčerpaná</div>
                <div className="text-sm text-gray-300 mb-3">
                  {error.payload?.message || "Tvoj plán nemá dosť kreditov na tento scan."}
                  {error.payload?.urls_in_sitemap != null && (
                    <span className="block mt-1 text-xs text-gray-400">
                      Sitemap obsahuje {error.payload.urls_in_sitemap} URL · zostáva ti {error.payload?.remaining ?? 0}.
                    </span>
                  )}
                </div>
                <a
                  href="/pricing#eshop"
                  className="inline-flex items-center gap-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm"
                >
                  Upgrade plánu <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {error?.type === "no_sitemap" && (
          <div className="p-6 bg-amber-900/20 border border-amber-700/40 rounded-sm mb-8" data-testid="eshop-scan-no-sitemap-error">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <div className="font-bold text-amber-200 mb-1">Sitemap sa nedá nájsť</div>
                <div className="text-sm text-gray-300">
                  {error.payload?.message}
                </div>
                {error.payload?.sitemaps_tried?.length > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Skúšali sme: {error.payload.sitemaps_tried.join(", ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error?.type === "generic" && (
          <div className="p-4 bg-rose-900/20 border border-rose-700/40 rounded-sm mb-8 text-sm text-rose-200" data-testid="eshop-scan-error">
            {error.message}
          </div>
        )}

        {/* Report */}
        {report && !scanning && (
          <>
            <EshopScanReport report={report} userEmail={userEmail} />

            {/* Mass GEO Page Generator — sits below the scan report
                so the user moves from "what's broken" (scan) to
                "fix it at scale" (generate localized pages) without
                leaving the screen. */}
            <div className="mt-10 pt-8 border-t border-gray-800" data-testid="eshop-geo-section">
              <EshopGeoGenerator report={report} userEmail={userEmail} />
            </div>
          </>
        )}

        {/* History — only when we have any AND not currently showing a fresh report */}
        {history.length > 0 && (
          <section className="mt-12 p-5 bg-gray-900/40 border border-gray-800 rounded-sm" data-testid="eshop-scanner-history">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gray-400" />
              <div className="text-sm font-bold text-gray-300">Predchádzajúce scany</div>
            </div>
            <ul className="divide-y divide-gray-900">
              {history.map((h) => (
                <li key={h.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-purple-300 truncate">{h.domain}</div>
                    <div className="text-[10px] text-gray-500">
                      Skóre {h.summary?.score ?? "–"} · {h.summary?.scanned ?? 0} URL ·{" "}
                      {new Date(h.created_at).toLocaleString("sk-SK")}
                    </div>
                  </div>
                  <button
                    onClick={() => handleLoadHistory(h.id)}
                    data-testid={`eshop-history-load-${h.id}`}
                    className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-white rounded-sm flex items-center gap-1"
                  >
                    Otvoriť <ArrowRight className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* AI Fix promo when no scan started yet */}
        {!report && !scanning && !error && (
          <section className="mt-12 p-6 bg-gradient-to-br from-purple-900/20 to-black border border-purple-700/40 rounded-sm" data-testid="eshop-scanner-promo">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-[10px] uppercase tracking-widest text-purple-400">
                Čo dostaneš
              </span>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-300">
              <li className="p-4 bg-black/40 border border-gray-800 rounded-sm">
                <div className="text-purple-400 font-bold mb-1">Per-URL známka A–F</div>
                <div className="text-xs text-gray-400">
                  Každú stránku ohodnotíme podľa 9 kritérií (title, meta, H1, schema, hreflang, alt, canonical, price markup).
                </div>
              </li>
              <li className="p-4 bg-black/40 border border-gray-800 rounded-sm">
                <div className="text-purple-400 font-bold mb-1">Top issues priority</div>
                <div className="text-xs text-gray-400">
                  Zoznam 10 najčastejších chýb naprieč e-shopom — fix jeden, zlepšíš desiatky URL naraz.
                </div>
              </li>
              <li className="p-4 bg-black/40 border border-gray-800 rounded-sm">
                <div className="text-purple-400 font-bold mb-1">✨ AI Fix per row</div>
                <div className="text-xs text-gray-400">
                  Pre každý issue dostaneš copy-paste HTML/JSON, ktorý môžeš vložiť priamo do template.
                </div>
              </li>
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
